/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { useEffect, useRef, useState } from "react";

/** One poll: answers whether to poll again; `isCurrent` turns false once its answer is stale. */
export type PollTick = (isCurrent: () => boolean) => Promise<boolean>;

/**
 * Runs `tick` while `active`, each run starting `delayMillis` after the previous one settled, so
 * reads never overlap and an older answer never lands after a newer one. The first run waits
 * `delayMillis` too unless `immediate`. Unmounting, turning inactive or hiding the tab starts no
 * further run; a tab shown again starts over.
 */
export function usePolling(
  tick: PollTick,
  { delayMillis, active = true, immediate = false }: {
    delayMillis: number;
    active?: boolean;
    immediate?: boolean;
  },
): void {
  const tickRef = useRef(tick);
  tickRef.current = tick;
  const visible = useDocumentVisible();

  useEffect(() => {
    if (!active || !visible) {
      return;
    }
    let current = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const isCurrent = () => current;
    const schedule = () => {
      timer = setTimeout(() => void run(), delayMillis);
    };
    const run = async () => {
      const again = await tickRef.current(isCurrent);
      if (again && current) {
        schedule();
      }
    };
    if (immediate) {
      void run();
    } else {
      schedule();
    }
    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [active, visible, delayMillis, immediate]);
}

/** Whether the tab is in front; a hidden tab asks the server nothing and picks up when shown. */
function useDocumentVisible(): boolean {
  const [visible, setVisible] = useState(() => document.visibilityState !== "hidden");
  useEffect(() => {
    const update = () => setVisible(document.visibilityState !== "hidden");
    document.addEventListener("visibilitychange", update);
    return () => document.removeEventListener("visibilitychange", update);
  }, []);
  return visible;
}
